import produce from 'immer';
import { ICompanyInitialState, types } from './types';

const initialState: ICompanyInitialState = {
    companyDetails: undefined,
    gettingCompanyDetails: false,
    gettingCompanyDetailsError: null,

    creatingCompany: false,
    creatingCompanyError: null,

    updatingCompany: false,
    updatingCompanyError: null,

    deletingCompany: false,
    deletingCompanyError: null,

    // Search companies state
    searchCompaniesData: undefined,
    searchingCompanies: false,
    searchingCompaniesError: null,

    addingReview: false,
    addingReviewError: null,

    editingReview: false,
    editingReviewError: null,

    likingReview: false,
    likingReviewError: null,

    deletingReview: false,
    deletingReviewError: null,

    // Position management state
    creatingPosition: false,
    creatingPositionError: null,

    approvingPosition: false,
    approvingPositionError: null,

    deletingPosition: false,
    deletingPositionError: null,
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;

    switch (type) {
        case types.GET_COMPANY_DETAILS:
            return produce(state, draft => {
                if (!payload.singleFetch) {
                    draft.gettingCompanyDetails = true;
                    draft.companyDetails = undefined;
                }
                draft.gettingCompanyDetailsError = null;
            });
        case types.GET_COMPANY_DETAILS_SUCCESS:
            return produce(state, draft => {
                const { data, singleFetch } = payload;

                if (singleFetch && draft.companyDetails) {
                    // For singleFetch, only update the specific sections that were requested

                    // Update only the sections that exist in the data
                    if (data.reviews) {
                        draft.companyDetails.reviews = data.reviews;
                    }
                    if (data.auditions) {
                        draft.companyDetails.auditions = data.auditions;
                    }
                    if (data.jobs) {
                        draft.companyDetails.jobs = data.jobs;
                    }
                    if (data.campaigns) {
                        draft.companyDetails.campaigns = data.campaigns;
                    }
                    if (data.leadership) {
                        draft.companyDetails.leadership = data.leadership;
                    }
                    if (data.similarCompanies) {
                        draft.companyDetails.similarCompanies = data.similarCompanies;
                    }
                } else {
                    // For full fetch, replace the entire companyDetails
                    draft.companyDetails = data;
                }
                draft.gettingCompanyDetails = false;
            });
        case types.GET_COMPANY_DETAILS_FAIL:
            return produce(state, draft => {
                draft.gettingCompanyDetails = false;
                draft.gettingCompanyDetailsError = payload;
            });

        case types.CREATE_COMPANY:
            return produce(state, draft => {
                draft.creatingCompany = true;
                draft.creatingCompanyError = null;
            });
        case types.CREATE_COMPANY_SUCCESS:
            return produce(state, draft => {
                draft.creatingCompany = false;
                // Update company details if we have them
                if (draft.companyDetails) {
                    draft.companyDetails.company = payload;
                }
            });
        case types.CREATE_COMPANY_FAIL:
            return produce(state, draft => {
                draft.creatingCompany = false;
                draft.creatingCompanyError = payload;
            });

        case types.UPDATE_COMPANY:
            return produce(state, draft => {
                draft.updatingCompany = true;
                draft.updatingCompanyError = null;
            });
        case types.UPDATE_COMPANY_SUCCESS:
            return produce(state, draft => {
                draft.updatingCompany = false;
                // Update company details if we have them
                if (draft.companyDetails) {
                    draft.companyDetails.company = payload;
                }
            });
        case types.UPDATE_COMPANY_FAIL:
            return produce(state, draft => {
                draft.updatingCompany = false;
                draft.updatingCompanyError = payload;
            });

        case types.DELETE_COMPANY:
            return produce(state, draft => {
                draft.deletingCompany = true;
                draft.deletingCompanyError = null;
            });
        case types.DELETE_COMPANY_SUCCESS:
            return produce(state, draft => {
                draft.deletingCompany = false;
                draft.companyDetails = undefined;
            });
        case types.DELETE_COMPANY_FAIL:
            return produce(state, draft => {
                draft.deletingCompany = false;
                draft.deletingCompanyError = payload;
            });

        case types.ADD_COMPANY_REVIEW:
            return produce(state, draft => {
                draft.addingReview = true;
                draft.addingReviewError = null;
            });
        case types.ADD_COMPANY_REVIEW_SUCCESS:
            return produce(state, draft => {
                draft.addingReview = false;
                // Add the new review to the reviews list
                if (draft.companyDetails?.reviews) {
                    draft.companyDetails.reviews.data.unshift(payload);
                    draft.companyDetails.reviews.pagination.totalCount += 1;
                }
            });
        case types.ADD_COMPANY_REVIEW_FAIL:
            return produce(state, draft => {
                draft.addingReview = false;
                draft.addingReviewError = payload;
            });

        case types.EDIT_COMPANY_REVIEW:
            return produce(state, draft => {
                draft.editingReview = true;
                draft.editingReviewError = null;
            });
        case types.EDIT_COMPANY_REVIEW_SUCCESS:
            return produce(state, draft => {
                draft.editingReview = false;
                // Update the review in the reviews list
                if (draft.companyDetails?.reviews) {
                    const reviewIndex = draft.companyDetails.reviews.data.findIndex(
                        review => review._id === payload._id,
                    );
                    if (reviewIndex > -1) {
                        draft.companyDetails.reviews.data[reviewIndex] = payload;
                    }
                }
            });
        case types.EDIT_COMPANY_REVIEW_FAIL:
            return produce(state, draft => {
                draft.editingReview = false;
                draft.editingReviewError = payload;
            });

        case types.LIKE_COMPANY_REVIEW:
            return produce(state, draft => {
                const { reviewId, like_type } = payload;
                draft.likingReview = true;
                draft.likingReviewError = null;

                // Optimistic update
                if (draft.companyDetails?.reviews) {
                    const reviewIndex = draft.companyDetails.reviews.data.findIndex(review => review._id === reviewId);
                    if (reviewIndex > -1) {
                        const review = draft.companyDetails.reviews.data[reviewIndex];

                        // Reset previous like/dislike state
                        review.is_user_liked = false;
                        review.is_user_disliked = false;
                        review.user_like = [];
                        review.user_dislike = [];

                        // Set new like/dislike state
                        if (like_type === 'like') {
                            review.is_user_liked = true;
                            review.user_like = [{ _id: 'optimistic', optimistic: true } as any];
                            review.total_likes = (review.total_likes || 0) + 1;
                        } else if (like_type === 'dislike') {
                            review.is_user_disliked = true;
                            review.user_dislike = [{ _id: 'optimistic', optimistic: true } as any];
                            review.total_dislikes = (review.total_dislikes || 0) + 1;
                        }
                    }
                }
            });
        case types.LIKE_COMPANY_REVIEW_SUCCESS:
            return produce(state, draft => {
                draft.likingReview = false;
                // Update the review in the reviews list with server response
                if (draft.companyDetails?.reviews) {
                    const reviewIndex = draft.companyDetails.reviews.data.findIndex(
                        review => review._id === payload._id,
                    );
                    if (reviewIndex > -1) {
                        draft.companyDetails.reviews.data[reviewIndex] = payload;
                    }
                }
            });
        case types.LIKE_COMPANY_REVIEW_FAIL:
            return produce(state, draft => {
                const { reviewId, like_type } = payload;
                draft.likingReview = false;
                draft.likingReviewError = payload;

                // Revert optimistic update
                if (draft.companyDetails?.reviews) {
                    const reviewIndex = draft.companyDetails.reviews.data.findIndex(review => review._id === reviewId);
                    if (reviewIndex > -1) {
                        const review = draft.companyDetails.reviews.data[reviewIndex];

                        // Revert the optimistic changes
                        if (like_type === 'like') {
                            review.is_user_liked = false;
                            review.user_like = [];
                            review.total_likes = Math.max((review.total_likes || 0) - 1, 0);
                        } else if (like_type === 'dislike') {
                            review.is_user_disliked = false;
                            review.user_dislike = [];
                            review.total_dislikes = Math.max((review.total_dislikes || 0) - 1, 0);
                        }
                    }
                }
            });

        case types.DELETE_COMPANY_REVIEW:
            return produce(state, draft => {
                draft.deletingReview = true;
                draft.deletingReviewError = null;
            });
        case types.DELETE_COMPANY_REVIEW_SUCCESS:
            return produce(state, draft => {
                draft.deletingReview = false;
                // Remove the review from the reviews list
                if (draft.companyDetails?.reviews) {
                    draft.companyDetails.reviews.data = draft.companyDetails.reviews.data.filter(
                        review => review._id !== payload,
                    );
                    draft.companyDetails.reviews.pagination.totalCount -= 1;
                }
            });
        case types.DELETE_COMPANY_REVIEW_FAIL:
            return produce(state, draft => {
                draft.deletingReview = false;
                draft.deletingReviewError = payload;
            });

        case types.DELETE_COMPANY_REVIEW_LIKE:
            return produce(state, draft => {
                const { additionalPayload, morePayload } = payload;
                draft.likingReview = true;

                // Optimistic update - remove the like/dislike
                if (draft.companyDetails?.reviews) {
                    const reviewIndex = draft.companyDetails.reviews.data.findIndex(
                        review => review._id === additionalPayload,
                    );
                    if (reviewIndex > -1) {
                        const review = draft.companyDetails.reviews.data[reviewIndex];

                        if (morePayload?.like_type === 'like') {
                            review.is_user_liked = false;
                            review.user_like = [];
                            review.total_likes = Math.max((review.total_likes || 0) - 1, 0);
                        } else if (morePayload?.like_type === 'dislike') {
                            review.is_user_disliked = false;
                            review.user_dislike = [];
                            review.total_dislikes = Math.max((review.total_dislikes || 0) - 1, 0);
                        }
                    }
                }
            });

        case types.DELETE_COMPANY_REVIEW_LIKE_SUCCESS:
            return produce(state, draft => {
                draft.likingReview = false;
                // Optimistic update already applied, no additional action needed
            });

        case types.DELETE_COMPANY_REVIEW_LIKE_FAIL:
            return produce(state, draft => {
                draft.likingReview = false;
                draft.likingReviewError = payload;
            });

        // Position management cases
        case types.CREATE_POSITION:
            return produce(state, draft => {
                draft.creatingPosition = true;
                draft.creatingPositionError = null;
            });
        case types.CREATE_POSITION_SUCCESS:
            return produce(state, draft => {
                draft.creatingPosition = false;
                // Add the new position to the company's leadership array
                if (draft.companyDetails?.leadership) {
                    const leadership = draft.companyDetails.leadership;
                    if (leadership) {
                        leadership.data.push(payload);
                        leadership.pagination.totalCount += 1;
                    }
                }
            });
        case types.CREATE_POSITION_FAIL:
            return produce(state, draft => {
                draft.creatingPosition = false;
                draft.creatingPositionError = payload;
            });

        case types.APPROVE_POSITION:
            return produce(state, draft => {
                draft.approvingPosition = true;
                draft.approvingPositionError = null;
            });
        case types.APPROVE_POSITION_SUCCESS:
            return produce(state, draft => {
                draft.approvingPosition = false;
                // Update the position's approved status in both leadership and otherPositions arrays
                if (draft.companyDetails?.leadership) {
                    const leadership = draft.companyDetails.leadership;
                    if (leadership) {
                        const positionIndex = leadership.data.findIndex(pos => pos._id === payload._id);
                        if (positionIndex !== -1) {
                            leadership.data[positionIndex] = payload;
                        }
                    }
                }
                if (draft.companyDetails?.otherPositions) {
                    const otherPositions = draft.companyDetails.otherPositions;
                    if (otherPositions) {
                        const positionIndex = otherPositions.data.findIndex(pos => pos._id === payload._id);
                        if (positionIndex !== -1) {
                            otherPositions.data[positionIndex] = payload;
                        }
                    }
                }
            });
        case types.APPROVE_POSITION_FAIL:
            return produce(state, draft => {
                draft.approvingPosition = false;
                draft.approvingPositionError = payload;
            });

        case types.DELETE_POSITION:
            return produce(state, draft => {
                draft.deletingPosition = true;
                draft.deletingPositionError = null;
            });
        case types.DELETE_POSITION_SUCCESS:
            return produce(state, draft => {
                draft.deletingPosition = false;
                // Remove the position from both leadership and otherPositions arrays
                if (draft.companyDetails?.leadership) {
                    const leadership = draft.companyDetails.leadership;
                    if (leadership) {
                        leadership.data = leadership.data.filter(pos => pos._id !== payload.positionId);
                        leadership.pagination.totalCount = Math.max(0, leadership.pagination.totalCount - 1);
                    }
                }
                if (draft.companyDetails?.otherPositions) {
                    const otherPositions = draft.companyDetails.otherPositions;
                    if (otherPositions) {
                        otherPositions.data = otherPositions.data.filter(pos => pos._id !== payload.positionId);
                        otherPositions.pagination.totalCount = Math.max(0, otherPositions.pagination.totalCount - 1);
                    }
                }
            });
        case types.DELETE_POSITION_FAIL:
            return produce(state, draft => {
                draft.deletingPosition = false;
                draft.deletingPositionError = payload;
            });

        case types.SEARCH_COMPANIES:
            return produce(state, draft => {
                draft.searchingCompanies = true;
                draft.searchingCompaniesError = null;
            });
        case types.SEARCH_COMPANIES_SUCCESS:
            return produce(state, draft => {
                draft.searchingCompanies = false;
                draft.searchCompaniesData = payload[0].results;
            });
        case types.SEARCH_COMPANIES_FAIL:
            return produce(state, draft => {
                draft.searchingCompanies = false;
                draft.searchingCompaniesError = payload;
            });

        default:
            return state;
    }
};

export { reducer };
